import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table } from "antd";
import type { TableColumnsType } from "antd";
import { ApolloError } from "@apollo/client";
import dayjs from "dayjs";

import routeNames from "constants/route-names";
import { useGetDeviceDetectionJobsByOrganizationIdsQuery } from "graphql/queries/get-device-detection-jobs-by-organization-ids.generated";
import { Device_Detection_Jobs } from "graphql/generated/graphql";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";

const tableColumns: TableColumnsType = [
	{
		title: "PROPERTY",
		render: (field, record, index) => {
			if (record.property && record.property.name) {
				return `${record.property.name}`;
			}
			return ``;
		},
		key: "PROPERTY"
	},
	{
		title: "AUTHOR",
		dataIndex: "author",
		render: (field, record, index) => {
			if (record.author) {
				return `${record.author?.first_name} ${record.author?.last_name}`;
			}
			return ``;
		},
		key: `AUTHOR`
	},
	{
		title: "ID",
		dataIndex: "id",
		key: `ID`
	},
	{
		title: "DEVICES",
		dataIndex: "detected_devices",
		render: detected_devices => {
			if (detected_devices) {
				const devices = typeof detected_devices === "string" ? JSON.parse(detected_devices) : detected_devices;

				return devices.length;
			}
			return 0;
		},
		key: `DEVICES`
	},
	{
		title: "INSTALLED",
		dataIndex: "detected_installed_products",
		render: detected_installed_products => {
			if (detected_installed_products) {
				const devices =
					typeof detected_installed_products === "string"
						? JSON.parse(detected_installed_products)
						: detected_installed_products;

				return devices.length;
			}
			return 0;
		},
		key: `INSTALLED`
	},
	{
		title: "STATUS",
		dataIndex: "status",
		key: `STATUS`
	},
	{
		title: "STARTED",
		dataIndex: "created_at",
		render: item => {
			const formattedDate = dayjs(item).format("MM/DD/YYYY HH:MM");
			return formattedDate;
		},
		key: `STARTED`
	}
];

const AdminDeviceDetections: React.FC = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const [dataSource, setDataSource] = useState<Array<Device_Detection_Jobs>>([]);

	const handleOnCompleteGetDevicesDetectionJobs = useCallback(data => {
		const { device_detection_jobs } = data;

		console.log("handleOnCompleteGetDevicesDetectionJobs data: ", data);

		setDataSource(device_detection_jobs);
	}, []);

	const handleOnErrorGetDevicesDetectionJobs = useCallback((error: ApolloError) => {
		console.log("handleOnErrorGetDevicesDetectionJobs Error: ", error);
	}, []);

	const { data, loading, error } = useGetDeviceDetectionJobsByOrganizationIdsQuery({
		variables: {
			organization_ids: loggedUserValue.user_properties.map(prop => prop.property?.organization_id)
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetDevicesDetectionJobs,
		onCompleted: handleOnCompleteGetDevicesDetectionJobs
	});

	const handleOnDetectionJob = useCallback(record => {
		const { id } = record;
		const path = `${routeNames.ADMIN_DEVICE_DETECTION_DETAILS.replace(":jobId", id)}`;

		navigate(path);
	}, []);

	const isLoading = loading;

	return (
		<div className="admin-products-screen-container">
			<span className="page-title">{"Automagical Device Detection"}</span>
			<div className="admin-products-content-container">
				<div className="admin-device-detection-filters">
					<Link to={routeNames.ADMIN_DEVICE_DETECTION_ADD} className="highnote-button">
						Add Devices
					</Link>
				</div>

				<div className="admin-properties-table-container">
					<Table
						dataSource={dataSource}
						columns={tableColumns}
						loading={isLoading}
						rowKey={record => record.id}
						onRow={(record, rowIndex) => {
							return {
								onClick: event => {
									handleOnDetectionJob(record);
								}
							};
						}}
						style={{ marginBottom: "25px" }}
					/>
				</div>
			</div>
		</div>
	);
};

export default AdminDeviceDetections;
